import { Suspense, useEffect, useRef, useState } from 'react';
import { UIComponents } from './ui-components';
import { useLoaderData } from '@remix-run/react';
import { type loader } from '~/routes/_index.tsx';
import {
  getIsSafariBrowser,
  isElementInViewport,
} from '~/services/layout/layout';
import { type UIComponentType } from '~/services/layout';
import {
  enqueueLoad,
  getSharedObserver,
  sharedObserverListeners,
} from '~/utils/get-shared-observer';

const LAZY_LOAD_ON_SCROLL_INTERSECTION_OBSERVER_THRESHOLD = 0.2;
const COMPONENTS_PLACEHOLDER_MIN_HEIGHT = 200;
const HERO_COMPONENT_PLACEHOLDER_MIN_HEIGHT = 400;

/**
 * Lazy load a component on scroll.
 * Immediately loads the component if it is in the viewport, then it sets up an
 * intersection observer to load the component when it comes into view.
 * @param componentType - The type of the component to load.
 * @param props - The props to pass to the component.
 * @returns A JSX element that loads the component when it comes into view.
 */
export function LazyLoadOnScroll({
  componentType,
  props = {},
}: {
  componentType: UIComponentType;
  props?: Record<string, any>;
}) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const { defaultThemeV2Config } = useLoaderData<typeof loader>();

  const enableLazyLoadingOnScrollSafari: boolean =
    defaultThemeV2Config.enable_lazy_loading_on_scroll_safari;

  useEffect(() => {
    if (shouldLoad) return;

    const el = ref.current;

    if (!el) return;

    const isSafari: boolean = getIsSafariBrowser();

    if (enableLazyLoadingOnScrollSafari && !isSafari) {
      setShouldLoad(true);
      return;
    }

    if (isElementInViewport(el)) {
      setShouldLoad(true);
      return;
    }

    const observer: IntersectionObserver | null = getSharedObserver({
      threshold: LAZY_LOAD_ON_SCROLL_INTERSECTION_OBSERVER_THRESHOLD,
    });

    if (!observer) {
      setShouldLoad(true);
      return;
    }

    sharedObserverListeners.set(el, () => {
      enqueueLoad(() => setShouldLoad(true));
    });

    observer.observe(el);

    return () => {
      sharedObserverListeners.delete(el);
      observer.unobserve(el);
    };
  }, [shouldLoad, enableLazyLoadingOnScrollSafari]);

  if (!shouldLoad) {
    const minHeight =
      componentType === 'hero_v2'
        ? HERO_COMPONENT_PLACEHOLDER_MIN_HEIGHT
        : COMPONENTS_PLACEHOLDER_MIN_HEIGHT;

    return <div ref={ref} style={{ minHeight }} />;
  }

  const Component = UIComponents[componentType];

  if (!Component) return null;

  return (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  );
}
